import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import FlagRanking from "../components/FlagRanking";
import api from "../lib/api";
import { toast } from "sonner";
import {
  ChevronRight, ChevronDown, ChevronLeft,
  Mic, ShieldOff, ShieldCheck, KeyRound, Trash2,
  CreditCard, X, Loader2, Check, Ban, Bell, BarChart3,
  Sparkles, CalendarPlus, Clock,
} from "lucide-react";

// Whole days remaining until an ISO trial-end date (0 if today, null if unset).
const trialDaysLeft = (iso) =>
  iso ? Math.max(0, Math.ceil((new Date(iso) - Date.now()) / 86400000)) : null;

const COMPANY_STATUS_META = {
  pending:  { label: "Pending",         cls: "bg-amber-50 text-amber-700 border-amber-300" },
  approved: { label: "Awaiting payment", cls: "bg-[#0025D8]/10 text-[#0025D8] border-[#0025D8]/30" },
  active:   { label: "Active",          cls: "bg-[#10B981]/10 text-[#047857] border-[#10B981]/30" },
  past_due: { label: "Past due",        cls: "bg-[#E11D48]/10 text-[#E11D48] border-[#E11D48]/30" },
  canceled: { label: "Canceled",        cls: "bg-secondary text-muted-foreground border-border" },
};

function StatusBadge({ status }) {
  const meta = COMPANY_STATUS_META[status] || COMPANY_STATUS_META.pending;
  return (
    <span className={`font-mono text-[9px] font-bold uppercase px-2 py-0.5 rounded-xl border ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function AssignPlanModal({ company, plans, busy, onClose, onSubmit }) {
  // Detect the currently-assigned plan's interval so the toggle opens on the right tab.
  const currentIsYearly = (company.company_plan || "").includes("yearly")
    || (plans.find((p) => p.id === company.company_plan)?.interval === "year");
  const [interval, setInterval] = useState(currentIsYearly ? "year" : "month");
  const intervalPlans = plans.filter((p) => (p.interval || "month") === interval);
  const [plan, setPlan] = useState(
    company.company_plan && !company.company_plan.startsWith("custom") ? company.company_plan : (intervalPlans[0]?.id || "starter")
  );
  const [customPrice, setCustomPrice] = useState("");
  const [customSeats, setCustomSeats] = useState("");
  const [customInterviews, setCustomInterviews] = useState("");
  const [mode, setMode] = useState("paid");   // "paid" | "trial"
  const [trialDays, setTrialDays] = useState("14");
  const isActive = company.company_status === "active";
  const isTrialMode = mode === "trial";
  const isCustom = plan === "custom" && !isTrialMode;
  const perLabel = interval === "year" ? "/yr" : "/mo";
  const priceLabel = interval === "year" ? "$/year" : "$/month";

  // Switching to trial mode: trials can't be custom — snap to a real tier.
  const switchMode = (next) => {
    setMode(next);
    if (next === "trial" && plan === "custom") {
      const first = plans.find((p) => (p.interval || "month") === interval);
      if (first) setPlan(first.id);
    }
  };

  // When the interval toggle flips, snap the selection to that interval's first tier
  // (unless Custom is selected — Custom carries its own interval via the toggle).
  const switchInterval = (next) => {
    setInterval(next);
    if (!isCustom) {
      const first = plans.find((p) => (p.interval || "month") === next);
      if (first) setPlan(first.id);
    }
  };

  const submit = () => {
    if (isTrialMode) {
      const days = parseInt(trialDays, 10);
      if (!days || days < 1 || days > 365) { toast.error("Enter a trial length of 1–365 days"); return; }
      onSubmit(company.user_id, { mode: "trial", plan, trial_days: days });
      return;
    }
    if (isCustom) {
      const cents = Math.round(parseFloat(customPrice) * 100);
      if (!cents || cents < 100) { toast.error(`Enter a ${interval === "year" ? "yearly" : "monthly"} price of at least $1`); return; }
      const seats = parseInt(customSeats, 10);
      const ivs = parseInt(customInterviews, 10);
      if (!seats || seats < 1) { toast.error("Enter at least 1 seat"); return; }
      if (!ivs || ivs < 1) { toast.error("Enter at least 1 interview"); return; }
      onSubmit(company.user_id, {
        plan: "custom", custom_price_cents: cents, custom_seats: seats,
        custom_interviews: ivs, custom_interval: interval,
      });
    } else {
      onSubmit(company.user_id, { plan });
    }
  };

  // Trials use a real tier's limits (no custom) — hide the Custom option in trial mode.
  const options = isTrialMode
    ? intervalPlans
    : [...intervalPlans, { id: "custom", label: "Custom", price_cents: null }];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl border border-border w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <div>
            <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#0025D8]">
              {isTrialMode ? "Start free trial" : isActive ? "Change plan" : "Approve & assign plan"}
            </div>
            <div className="font-semibold text-sm mt-0.5 truncate">{company.name}</div>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-2">
          {/* Paid vs Trial mode toggle */}
          <div className="flex items-center gap-1 p-1 rounded-xl bg-secondary mb-3">
            {[["paid", "Paid plan"], ["trial", "Free trial"]].map(([val, lbl]) => (
              <button
                key={val}
                onClick={() => switchMode(val)}
                className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                  mode === val
                    ? (val === "trial" ? "bg-white text-[#7C3AED] shadow-sm" : "bg-white text-[#0025D8] shadow-sm")
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {lbl}
              </button>
            ))}
          </div>

          {isTrialMode && (
            <div className="rounded-xl border border-[#7C3AED]/30 bg-[#7C3AED]/5 px-3 py-2.5 mb-3">
              <p className="text-[11px] text-[#6D28D9] leading-snug">
                Grants full access with the selected tier's limits — <strong>no payment</strong>.
                When the trial ends the account locks to “awaiting payment,” and they can subscribe in one click.
              </p>
            </div>
          )}

          {/* Billing interval toggle */}
          <div className="flex items-center gap-1 p-1 rounded-xl bg-secondary mb-3">
            {[["month", "Monthly"], ["year", "Yearly"]].map(([val, lbl]) => (
              <button
                key={val}
                onClick={() => switchInterval(val)}
                className={`flex-1 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
                  interval === val ? "bg-white text-[#0025D8] shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {lbl}
              </button>
            ))}
          </div>

          {options.map((p) => {
            const selected = plan === p.id;
            return (
              <button
                key={p.id}
                onClick={() => setPlan(p.id)}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl border text-left transition-colors ${
                  selected ? "border-[#0025D8] bg-[#0025D8]/5" : "border-border hover:bg-secondary"
                }`}
              >
                <div className="min-w-0">
                  <div className="text-sm font-semibold flex items-center gap-2">
                    {p.label}
                    {selected && <Check className="w-3.5 h-3.5 text-[#0025D8]" />}
                  </div>
                  {p.id !== "custom" ? (
                    <div className="font-mono text-[10px] text-muted-foreground mt-0.5">
                      {isTrialMode
                        ? <><span className="text-[#7C3AED] font-semibold">Free</span> · {p.seats} seats · {p.interviews} interviews/mo</>
                        : <>${(p.price_cents / 100).toFixed(0)}{perLabel} · {p.seats} seats · {p.interviews} interviews/mo</>}
                    </div>
                  ) : (
                    <div className="font-mono text-[10px] text-muted-foreground mt-0.5">Set your own price, seats & interviews</div>
                  )}
                </div>
              </button>
            );
          })}

          {isTrialMode && (
            <div className="pt-2">
              <label className="block text-[10px] font-semibold text-muted-foreground mb-1">Trial length (days)</label>
              <div className="flex items-center gap-2">
                <input type="number" min="1" max="365" value={trialDays}
                  onChange={(e) => setTrialDays(e.target.value)} placeholder="14"
                  className="w-24 rounded-lg border border-border px-2.5 py-2 text-sm focus:border-[#7C3AED] outline-none" />
                <div className="flex gap-1">
                  {[7, 14, 30].map((d) => (
                    <button key={d} onClick={() => setTrialDays(String(d))}
                      className={`px-2.5 py-1.5 text-[11px] font-semibold rounded-lg border transition-colors ${
                        String(d) === trialDays ? "border-[#7C3AED] bg-[#7C3AED]/10 text-[#7C3AED]" : "border-border text-muted-foreground hover:bg-secondary"
                      }`}>{d}d</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {isCustom && (
            <>
              <div className="grid grid-cols-3 gap-2 pt-2">
                <div>
                  <label className="block text-[10px] font-semibold text-muted-foreground mb-1">{priceLabel}</label>
                  <input type="number" min="1" value={customPrice} onChange={(e) => setCustomPrice(e.target.value)} placeholder={interval === "year" ? "9999" : "999"}
                    className="w-full rounded-lg border border-border px-2.5 py-2 text-sm focus:border-[#0025D8] outline-none" />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-muted-foreground mb-1">Seats</label>
                  <input type="number" min="1" value={customSeats} onChange={(e) => setCustomSeats(e.target.value)} placeholder="25"
                    className="w-full rounded-lg border border-border px-2.5 py-2 text-sm focus:border-[#0025D8] outline-none" />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-muted-foreground mb-1">Interviews/mo</label>
                  <input type="number" min="1" value={customInterviews} onChange={(e) => setCustomInterviews(e.target.value)} placeholder="1000"
                    className="w-full rounded-lg border border-border px-2.5 py-2 text-sm focus:border-[#0025D8] outline-none" />
                </div>
              </div>
              <p className="font-mono text-[10px] text-muted-foreground pt-1">
                Billed {interval === "year" ? "yearly" : "monthly"} · interviews reset every month
              </p>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-border">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium rounded-lg border border-border hover:bg-secondary transition-colors">Cancel</button>
          <button onClick={submit} disabled={busy}
            className={`flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-60 ${
              isTrialMode ? "bg-[#7C3AED] hover:bg-[#6D28D9]" : "bg-[#0025D8] hover:bg-[#001CB3]"}`}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
            {isTrialMode ? "Start trial" : isActive ? "Apply change" : "Assign plan"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="bg-white border-r border-b border-border p-5 last:border-r-0">
      <div className="font-mono text-[9px] text-muted-foreground tracking-[0.25em] uppercase">{label}</div>
      <div className={`font-display text-3xl font-bold mt-1 ${accent || ""}`}>{value}</div>
    </div>
  );
}

function EmployeeRow({ emp, onToggleEnabled, onResetPassword, onDelete, busy }) {
  const enabled = emp.account_enabled !== false;
  return (
    <div className="grid grid-cols-12 border-b border-border items-center bg-white hover:bg-secondary/20 transition-colors">
      <div className="col-span-1 border-r border-border self-stretch border-l-2 border-l-[#7C3AED]/30" />
      <div className="col-span-3 p-3 border-r border-border min-w-0">
        <div className="font-medium text-sm truncate">{emp.name}</div>
      </div>
      <div className="col-span-3 p-3 border-r border-border font-mono text-[10px] text-muted-foreground truncate">
        {emp.email}
      </div>
      <div className="col-span-2 p-3 border-r border-border">
        <span className={`font-mono text-[9px] font-bold uppercase px-2 py-0.5 ${enabled ? "bg-[#10B981]/10 text-[#047857] rounded-xl border border-[#10B981]/30" : "bg-[#E11D48]/10 text-[#E11D48] rounded-xl border border-[#E11D48]/30"}`}>
          {enabled ? "ACTIVE" : "DISABLED"}
        </span>
      </div>
      <div className="col-span-2 p-3 border-r border-border font-mono text-[10px] text-muted-foreground">
        {emp.created_at ? new Date(emp.created_at).toLocaleDateString("en", { month: "short", day: "numeric", year: "numeric" }) : "—"}
      </div>
      <div className="col-span-1 p-3 flex items-center gap-1 justify-center">
        <button onClick={() => onToggleEnabled(emp, !enabled)} disabled={busy} title={enabled ? "Disable" : "Enable"}
          className={`p-1.5 rounded transition-colors ${enabled ? "text-[#F59E0B] hover:bg-[#F59E0B]/10" : "text-[#10B981] hover:bg-[#10B981]/10"}`}>
          {enabled ? <ShieldOff className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
        </button>
        <button onClick={() => onResetPassword(emp)} disabled={busy} title="Reset password"
          className="p-1.5 rounded text-muted-foreground hover:text-[#0025D8] hover:bg-[#0025D8]/5 transition-colors">
          <KeyRound className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => onDelete(emp)} disabled={busy} title="Delete"
          className="p-1.5 rounded text-muted-foreground hover:text-[#E11D48] hover:bg-[#E11D48]/5 transition-colors">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

export default function AdminCompanies() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [busy, setBusy] = useState(new Set());
  const [plans, setPlans] = useState([]);
  const [assignFor, setAssignFor] = useState(null);   // company being assigned a plan
  const [summary, setSummary] = useState(null);       // platform-wide analytics rollup
  const [overview, setOverview] = useState({});       // per-company volume/usage, keyed by user_id
  const [sortKey, setSortKey] = useState("name");     // name | this_month | cycle | total | avg_score
  const [trialsOnly, setTrialsOnly] = useState(false);
  const [soonOnly, setSoonOnly] = useState(false);    // trials expiring within 3 days

  useEffect(() => {
    api.get("/admin/users")
      .then(({ data }) => setUsers(data))
      .catch(() => toast.error("Could not load data"))
      .finally(() => setLoading(false));
    api.get("/admin/company-plans")
      .then(({ data }) => setPlans(data.plans || []))
      .catch(() => {});
    api.get("/admin/analytics/summary")
      .then(({ data }) => setSummary(data))
      .catch(() => {});
    api.get("/admin/companies/analytics-overview")
      .then(({ data }) => setOverview(data || {}))
      .catch(() => {});
  }, []);

  // Fraction of this-cycle allowance consumed (for sorting/highlighting upsell targets).
  const cycleFrac = (id) => {
    const o = overview[id]; if (!o || o.interview_limit == null) return -1;  // unlimited → sort last
    return o.interview_limit ? o.interviews_used_cycle / o.interview_limit : 0;
  };

  const TRIAL_SOON_DAYS = 3;
  const isTrialSoon = (u) => {
    if (!u.is_trial) return false;
    const d = trialDaysLeft(u.trial_ends_at);
    return d != null && d <= TRIAL_SOON_DAYS;
  };
  const trialCount = users.filter((u) => u.role === "company" && u.is_trial).length;
  const trialSoonCount = users.filter((u) => u.role === "company" && isTrialSoon(u)).length;
  const companies = users
    .filter((u) => u.role === "company"
      && (!trialsOnly || u.is_trial)
      && (!soonOnly || isTrialSoon(u)))
    .sort((a, b) => {
    const oa = overview[a.user_id] || {}, ob = overview[b.user_id] || {};
    switch (sortKey) {
      case "this_month": return (ob.interviews_this_month || 0) - (oa.interviews_this_month || 0);
      case "total":      return (ob.interviews_total || 0) - (oa.interviews_total || 0);
      case "avg_score":  return (ob.avg_score ?? -1) - (oa.avg_score ?? -1);
      case "cycle":      return cycleFrac(b.user_id) - cycleFrac(a.user_id);
      default:           return (a.name || "").localeCompare(b.name || "");
    }
  });
  const employeesByRoot = users
    .filter((u) => u.role === "company_employee")
    .reduce((acc, e) => {
      if (e.company_root_id) {
        if (!acc[e.company_root_id]) acc[e.company_root_id] = [];
        acc[e.company_root_id].push(e);
      }
      return acc;
    }, {});

  const allEmployees = users.filter((u) => u.role === "company_employee");
  const activeEmployees = allEmployees.filter((e) => e.account_enabled !== false);

  const toggleExpand = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const setBusyFor = (id, val) => setBusy((prev) => {
    const next = new Set(prev);
    val ? next.add(id) : next.delete(id);
    return next;
  });

  const handleToggleEnabled = async (user, enabled) => {
    setBusyFor(user.user_id, true);
    try {
      await api.patch(`/admin/users/${user.user_id}/toggle-enabled`, { enabled });
      setUsers((prev) => prev.map((u) => u.user_id === user.user_id ? { ...u, account_enabled: enabled } : u));
      toast.success(`${user.name} ${enabled ? "enabled" : "disabled"}`);
    } catch {
      toast.error("Could not update account status");
    } finally {
      setBusyFor(user.user_id, false);
    }
  };

  const handleResetPassword = async (user) => {
    if (!window.confirm(`Reset password for ${user.name}?`)) return;
    setBusyFor(user.user_id, true);
    try {
      const { data } = await api.post(`/admin/users/${user.user_id}/reset-password`, {});
      toast.success(`Temp password: ${data.temporary_password}`, { duration: 10000 });
    } catch {
      toast.error("Could not reset password");
    } finally {
      setBusyFor(user.user_id, false);
    }
  };

  const applyCompanyPatch = (companyId, patch) =>
    setUsers((prev) => prev.map((u) => u.user_id === companyId ? { ...u, ...patch } : u));

  const handleAssignPlan = async (companyId, payload) => {
    setBusyFor(companyId, true);
    try {
      if (payload.mode === "trial") {
        const { data } = await api.post(`/admin/companies/${companyId}/grant-trial`, {
          plan: payload.plan, trial_days: payload.trial_days,
        });
        applyCompanyPatch(companyId, {
          company_status: "active", company_plan: data.company_plan,
          plan_limits: data.plan_limits, is_trial: true, trial_ends_at: data.trial_ends_at,
        });
        toast.success(`${data.plan_label} started — ${payload.trial_days}-day free trial`);
        setAssignFor(null);
        return;
      }
      const { data } = await api.post(`/admin/companies/${companyId}/assign-plan`, payload);
      applyCompanyPatch(companyId, {
        company_status: data.company_status,
        company_plan: data.company_plan,
        plan_limits: data.plan_limits,
        is_trial: false, trial_ends_at: null,
      });
      toast.success(
        data.company_status === "active"
          ? `Plan changed to ${data.plan_label} (live)`
          : `${data.plan_label} assigned — company can now pay to activate`
      );
      setAssignFor(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not assign plan");
    } finally {
      setBusyFor(companyId, false);
    }
  };

  const handleExtendTrial = async (company) => {
    const input = window.prompt(`Extend ${company.name}'s trial by how many days?`, "7");
    if (input == null) return;
    const days = parseInt(input, 10);
    if (!days || days < 1 || days > 365) { toast.error("Enter 1–365 days"); return; }
    setBusyFor(company.user_id, true);
    try {
      const { data } = await api.post(`/admin/companies/${company.user_id}/extend-trial`, { days });
      applyCompanyPatch(company.user_id, { trial_ends_at: data.trial_ends_at, is_trial: true });
      toast.success(`Trial extended by ${days} day${days === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not extend trial");
    } finally {
      setBusyFor(company.user_id, false);
    }
  };

  const handleEndTrial = async (company) => {
    if (!window.confirm(`End ${company.name}'s trial now? They'll lock to “awaiting payment” and must subscribe to continue.`)) return;
    setBusyFor(company.user_id, true);
    try {
      await api.post(`/admin/companies/${company.user_id}/end-trial`);
      applyCompanyPatch(company.user_id, { company_status: "approved", is_trial: false, trial_ends_at: null });
      toast.success(`${company.name}'s trial ended`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not end trial");
    } finally {
      setBusyFor(company.user_id, false);
    }
  };

  const handleRevoke = async (company) => {
    if (!window.confirm(`Suspend ${company.name}? This cancels their subscription and locks the account back to pending.`)) return;
    setBusyFor(company.user_id, true);
    try {
      await api.post(`/admin/companies/${company.user_id}/revoke`);
      applyCompanyPatch(company.user_id, { company_status: "pending", company_plan: null, plan_limits: null });
      toast.success(`${company.name} suspended`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not suspend company");
    } finally {
      setBusyFor(company.user_id, false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete ${user.name} (${user.email})? This cannot be undone.`)) return;
    setBusyFor(user.user_id, true);
    try {
      await api.delete(`/admin/users/${user.user_id}`);
      setUsers((prev) => prev.filter((u) => u.user_id !== user.user_id));
      toast.success(`${user.name} deleted`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    } finally {
      setBusyFor(user.user_id, false);
    }
  };

  if (loading) return <div className="p-12 font-mono text-sm text-muted-foreground">Loading…</div>;

  return (
    <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 font-mono text-[10px] text-muted-foreground mb-6">
        <Link to="/admin" className="hover:text-foreground flex items-center gap-1">
          <ChevronLeft className="w-3 h-3" /> Admin
        </Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-foreground">Companies</span>
      </div>

      <div className="mb-8">
        <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#0025D8]">Companies · Control</div>
        <h1 className="font-display font-black text-4xl tracking-tight text-[#0F172A] mt-2">Companies</h1>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 border-l border-t border-border mb-6">
        <Stat label="Companies" value={companies.length} />
        <Stat label="Total Employees" value={allEmployees.length} />
        <Stat label="Active Employees" value={activeEmployees.length} accent="text-[#10B981]" />
      </div>

      {/* Platform-wide hiring funnel (aggregate, for product insight) */}
      {summary && (
        <div className="rounded-xl border border-border bg-white p-4 mb-10">
          <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-3">
            <BarChart3 className="w-3.5 h-3.5" /> Platform hiring funnel · all companies
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {[
              { label: "Roles", value: summary.roles },
              { label: "Applied", value: summary.applied },
              { label: "Shortlisted", value: summary.shortlisted },
              { label: "Invited", value: summary.invited },
              { label: "Completed", value: summary.completed },
              { label: "Avg score", value: summary.avg_score ?? "—", tone: "text-[#0025D8]" },
              { label: "Avg flags", value: summary.avg_flags ?? "—", tone: summary.avg_flags > 0 ? "text-[#E11D48]" : "text-[#0F172A]" },
            ].map((s) => (
              <div key={s.label}>
                <div className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground mb-0.5">{s.label}</div>
                <div className={`font-display font-bold text-xl ${s.tone || "text-[#0F172A]"}`}>{s.value}</div>
              </div>
            ))}
          </div>

          {summary.flag_ranking && summary.flag_ranking.length > 0 && (
            <div className="mt-4 pt-4 border-t border-border max-w-md">
              <FlagRanking ranking={summary.flag_ranking} title="Most common flags" subtitle="across all interviews" />
            </div>
          )}
        </div>
      )}

      {/* Trials filters */}
      {trialCount > 0 && (
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <button onClick={() => { setTrialsOnly((v) => !v); setSoonOnly(false); }}
            className={`inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold px-2.5 py-1 rounded-lg border transition-colors ${
              trialsOnly && !soonOnly ? "bg-[#7C3AED] text-white border-[#7C3AED]" : "text-[#7C3AED] border-[#7C3AED]/30 hover:bg-[#7C3AED]/10"
            }`}>
            <Sparkles className="w-3 h-3" /> {trialsOnly && !soonOnly ? "Showing trials" : "Trials only"} · {trialCount}
          </button>
          {trialSoonCount > 0 && (
            <button onClick={() => { const next = !soonOnly; setSoonOnly(next); setTrialsOnly(next); }}
              className={`inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold px-2.5 py-1 rounded-lg border transition-colors ${
                soonOnly ? "bg-[#F59E0B] text-white border-[#F59E0B]" : "text-[#B45309] border-[#F59E0B]/40 hover:bg-[#F59E0B]/10"
              }`}>
              <Clock className="w-3 h-3" /> Expiring ≤{TRIAL_SOON_DAYS}d · {trialSoonCount}
            </button>
          )}
        </div>
      )}

      {/* Sort control */}
      {companies.length > 0 && (
        <div className="flex items-center gap-2 mb-3">
          <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">Sort by</span>
          {[
            { k: "name", label: "Name" },
            { k: "cycle", label: "Cycle usage" },
            { k: "this_month", label: "This month" },
            { k: "total", label: "Total sent" },
            { k: "avg_score", label: "Avg score" },
          ].map((s) => (
            <button key={s.k} onClick={() => setSortKey(s.k)}
              className={`font-mono text-[10px] px-2 py-1 rounded transition-colors ${
                sortKey === s.k ? "bg-[#7C3AED] text-white" : "text-muted-foreground hover:bg-secondary"
              }`}>
              {s.label}
            </button>
          ))}
        </div>
      )}

      {companies.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center text-muted-foreground text-sm">
          {soonOnly ? `No trials expiring in the next ${TRIAL_SOON_DAYS} days.`
            : trialsOnly ? "No companies on a trial right now." : "No company accounts yet."}
        </div>
      ) : (
        <div className="border-l border-t border-border">
          {companies.map((company) => {
            const ov = overview[company.user_id] || {};
            const isExpanded = expanded.has(company.user_id);
            const emps = employeesByRoot[company.user_id] || [];
            const companyEnabled = company.account_enabled !== false;
            const isBusy = busy.has(company.user_id);
            return (
              <div key={company.user_id}>
                {/* Company row — click left area to expand, action buttons on right */}
                <div className="grid grid-cols-12 border-r border-b border-border items-stretch bg-white hover:bg-secondary/20 transition-colors cursor-pointer"
                  onClick={() => toggleExpand(company.user_id)}
                >
                  {/* Chevron */}
                  <div className="col-span-1 p-4 border-r border-border flex items-center justify-center">
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                  </div>

                  {/* Name + email */}
                  <div className="col-span-5 p-4 border-r border-border flex items-center gap-3">
                    <div className={`w-8 h-8 flex items-center justify-center font-bold text-sm flex-shrink-0 ${companyEnabled ? "bg-[#7C3AED] text-white" : "bg-muted text-muted-foreground"}`}>
                      {company.name?.[0]?.toUpperCase() || "?"}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate">{company.name}</div>
                      <div className="text-xs text-muted-foreground font-mono truncate">{company.email}</div>
                    </div>
                  </div>

                  {/* Billing status + plan */}
                  <div className="col-span-2 p-4 border-r border-border flex flex-col justify-center gap-1.5">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <StatusBadge status={company.company_status || "pending"} />
                      {company.is_trial && (
                        <span className="inline-flex items-center gap-1 text-[9px] font-semibold font-mono px-1.5 py-0.5 rounded border bg-[#7C3AED]/10 text-[#7C3AED] border-[#7C3AED]/30">
                          <Sparkles className="w-2.5 h-2.5" /> TRIAL{trialDaysLeft(company.trial_ends_at) != null ? ` · ${trialDaysLeft(company.trial_ends_at)}d` : ""}
                        </span>
                      )}
                      {company.activation_requested && (company.company_status === "pending" || !company.company_status) && (
                        <span className="inline-flex items-center gap-1 text-[9px] font-semibold font-mono px-1.5 py-0.5 rounded border bg-violet-50 text-violet-700 border-violet-300">
                          <Bell className="w-2.5 h-2.5" /> REQUESTED
                        </span>
                      )}
                    </div>
                    <div className="font-mono text-[10px] text-muted-foreground">
                      {company.company_plan
                        ? <>{plans.find((p) => p.id === company.company_plan)?.label || (company.company_plan === "custom" ? "Custom" : company.company_plan)}
                            {company.plan_limits ? ` · ${company.plan_limits.seats} seats / ${company.plan_limits.interviews} iv` : ""}
                          </>
                        : company.grandfathered ? "Grandfathered" : "No plan"}
                    </div>
                    {(company.addon_seats > 0 || company.interview_credits > 0) && (
                      <div className="font-mono text-[9px] text-[#0025D8]">
                        {company.addon_seats > 0 ? `+${company.addon_seats} seat${company.addon_seats !== 1 ? "s" : ""}` : ""}
                        {company.addon_seats > 0 && company.interview_credits > 0 ? " · " : ""}
                        {company.interview_credits > 0 ? `${company.interview_credits} credits` : ""}
                      </div>
                    )}
                  </div>

                  {/* Interview volume + cycle usage (upsell intel) */}
                  <div className="col-span-2 p-4 border-r border-border flex flex-col justify-center gap-1">
                    {(() => {
                      const lim = ov.interview_limit;
                      const used = ov.interviews_used_cycle || 0;
                      const over = lim != null && used >= lim;
                      const near = lim != null && !over && used / lim >= 0.8;
                      return (
                        <div className={`flex items-center gap-1.5 font-mono text-[10px] font-semibold ${over ? "text-[#E11D48]" : near ? "text-[#F59E0B]" : "text-foreground"}`}>
                          <Mic className="w-3 h-3" />
                          {lim == null ? `${used} · ∞` : `${used}/${lim}`}
                          {over && <span className="text-[8px] uppercase tracking-wider bg-[#E11D48] text-white px-1 rounded">upsell</span>}
                          {near && <span className="text-[8px] uppercase tracking-wider bg-[#F59E0B] text-white px-1 rounded">near</span>}
                        </div>
                      );
                    })()}
                    <div className="font-mono text-[9px] text-muted-foreground">
                      {ov.interviews_this_month || 0} this mo · {ov.interviews_total || 0} all
                      {ov.avg_score != null ? ` · ${ov.avg_score} avg` : ""}
                    </div>
                    <div className="font-mono text-[9px] text-muted-foreground/60">
                      {emps.length} emp{emps.length !== 1 ? "s" : ""}
                      {company.created_at ? ` · ${new Date(company.created_at).toLocaleDateString("en", { month: "short", year: "2-digit" })}` : ""}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="col-span-2 p-3 flex items-center gap-1 justify-center" onClick={(e) => e.stopPropagation()}>
                    <Link
                      to={`/admin/companies/${company.user_id}`}
                      title="View roles & interview analytics"
                      className="p-1.5 rounded text-[#7C3AED] hover:bg-[#7C3AED]/10 transition-colors"
                    >
                      <BarChart3 className="w-3.5 h-3.5" />
                    </Link>
                    <button
                      onClick={() => setAssignFor(company)}
                      disabled={isBusy}
                      title={company.company_status === "active" ? "Change plan / start trial" : "Approve, assign plan, or start a trial"}
                      className="p-1.5 rounded text-[#0025D8] hover:bg-[#0025D8]/10 transition-colors"
                    >
                      <CreditCard className="w-3.5 h-3.5" />
                    </button>
                    {company.is_trial && (
                      <>
                        <button
                          onClick={() => handleExtendTrial(company)}
                          disabled={isBusy}
                          title="Extend trial"
                          className="p-1.5 rounded text-[#7C3AED] hover:bg-[#7C3AED]/10 transition-colors"
                        >
                          <CalendarPlus className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleEndTrial(company)}
                          disabled={isBusy}
                          title="End trial now (lock to awaiting payment)"
                          className="p-1.5 rounded text-[#F59E0B] hover:bg-[#F59E0B]/10 transition-colors"
                        >
                          <Clock className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                    {["approved", "active", "past_due"].includes(company.company_status) && (
                      <button
                        onClick={() => handleRevoke(company)}
                        disabled={isBusy}
                        title="Suspend (cancel subscription, lock to pending)"
                        className="p-1.5 rounded text-[#F59E0B] hover:bg-[#F59E0B]/10 transition-colors"
                      >
                        <Ban className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => handleToggleEnabled(company, !companyEnabled)}
                      disabled={isBusy}
                      title={companyEnabled ? "Disable login" : "Enable login"}
                      className={`p-1.5 rounded transition-colors ${companyEnabled ? "text-[#F59E0B] hover:bg-[#F59E0B]/10" : "text-[#10B981] hover:bg-[#10B981]/10"}`}
                    >
                      {companyEnabled ? <ShieldOff className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                    </button>
                    <button
                      onClick={() => handleResetPassword(company)}
                      disabled={isBusy}
                      title="Reset password"
                      className="p-1.5 rounded text-muted-foreground hover:text-[#0025D8] hover:bg-[#0025D8]/5 transition-colors"
                    >
                      <KeyRound className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(company)}
                      disabled={isBusy}
                      title="Delete company"
                      className="p-1.5 rounded text-muted-foreground hover:text-[#E11D48] hover:bg-[#E11D48]/5 transition-colors"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-b border-border">
                    {company.activation_requested && company.activation_request_data && (
                      <div className="px-6 py-4 bg-violet-50 border-b border-violet-200">
                        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-violet-600 mb-2 flex items-center gap-1.5">
                          <Bell className="w-3 h-3" /> Activation request received
                          {company.activation_request_at && (
                            <span className="text-violet-400 normal-case tracking-normal">
                              · {new Date(company.activation_request_at).toLocaleString("en", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                          <div><span className="text-violet-500 font-semibold">Name</span><br />{company.activation_request_data.name}</div>
                          <div><span className="text-violet-500 font-semibold">Phone</span><br />{company.activation_request_data.phone}</div>
                          <div><span className="text-violet-500 font-semibold">Company</span><br />{company.activation_request_data.company_name}</div>
                          <div><span className="text-violet-500 font-semibold">Submitted by</span><br />{company.activation_request_data.submitted_by_email}</div>
                          {company.activation_request_data.message && (
                            <div className="col-span-2 sm:col-span-4">
                              <span className="text-violet-500 font-semibold">Message</span>
                              <p className="mt-0.5 text-foreground whitespace-pre-wrap">{company.activation_request_data.message}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {emps.length === 0 ? (
                      <div className="px-8 py-6 text-sm text-muted-foreground font-mono text-center bg-[#FAFAFA]">
                        No employees added yet.
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-12 border-b border-border bg-[#7C3AED]/5 font-mono text-[9px] uppercase tracking-[0.2em] text-muted-foreground">
                          <div className="col-span-1 border-r border-border" />
                          <div className="col-span-3 p-3 border-r border-border">Name</div>
                          <div className="col-span-3 p-3 border-r border-border">Email</div>
                          <div className="col-span-2 p-3 border-r border-border">Status</div>
                          <div className="col-span-2 p-3 border-r border-border">Joined</div>
                          <div className="col-span-1 p-3">Actions</div>
                        </div>
                        {emps.map((emp) => (
                          <EmployeeRow
                            key={emp.user_id}
                            emp={emp}
                            onToggleEnabled={handleToggleEnabled}
                            onResetPassword={handleResetPassword}
                            onDelete={handleDelete}
                            busy={busy.has(emp.user_id)}
                          />
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {assignFor && (
        <AssignPlanModal
          company={assignFor}
          plans={plans}
          busy={busy.has(assignFor.user_id)}
          onClose={() => setAssignFor(null)}
          onSubmit={handleAssignPlan}
        />
      )}
    </main>
  );
}
